// Person drawer — everything about one human in one place (iter268).
// Identity + role, contact details (moved off the old Account Manager /
// Project Manager records), roster flags, and their permission overrides.
import { useEffect, useState } from "react";
import { X, Save, ShieldCheck, Stethoscope, Check, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { updateUser, getUserAccess, setUserAccess, diagnoseAccount } from "@/lib/api";
import PasscodeDialog from "@/components/users/PasscodeDialog";
import { SubTabs, Callout, LoadingState } from "@/components/settings/SettingsUI";
import { formatPhoneNumber } from "@/lib/phoneFormat";

const ROLES = [
  ["admin", "Admin"], ["service_manager", "Service Manager"], ["project_manager", "Project Manager"],
  ["account_manager", "Account Manager"], ["accounting", "Accounting"], ["lead_technician", "Lead Technician"],
  ["technician", "Technician"], ["warehouse", "Warehouse (Receiving only)"], ["viewer", "Viewer (read-only)"],
];

const TABS = [
  { id: "identity", label: "Identity" },
  { id: "contact", label: "Contact" },
  { id: "roster", label: "Roster" },
  { id: "permissions", label: "Permissions" },
];

export default function UserDrawer({ user, onClose, onSaved, canWrite }) {
  const [tab, setTab] = useState("identity");
  const [form, setForm] = useState({
    name: user.name || "",
    role: (user.role === "office" ? "accounting" : user.role) || "viewer",
    login_enabled: user.login_enabled !== false,
    contact: { ...(user.contact || {}) },
    roster: { ...(user.roster || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState(null);
  const [passcodePrompt, setPasscodePrompt] = useState(false);

  useEffect(() => {
    if (tab !== "permissions" || access) return;
    getUserAccess(user.id).then(r => setAccess(r.data)).catch(() => setAccess({ error: true }));
  }, [tab, access, user.id]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setContact = (k, v) => setForm(prev => ({ ...prev, contact: { ...prev.contact, [k]: v } }));
  const setRoster = (k, v) => setForm(prev => ({ ...prev, roster: { ...prev.roster, [k]: v } }));

  const save = async (verifyToken) => {
    const roleChanged = form.role !== ((user.role === "office" ? "accounting" : user.role) || "viewer");
    if (roleChanged && user.passcode_protected && !verifyToken) {
      setPasscodePrompt(true);
      return;
    }
    setSaving(true);
    try {
      await updateUser(user.id, {
        name: form.name.trim(),
        role: form.role,
        login_enabled: form.login_enabled,
        contact: form.contact,
        roster: form.roster,
        ...(verifyToken ? { verify_token: verifyToken } : {}),
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveOverrides = async (surfaces) => {
    try {
      const r = await setUserAccess(user.id, surfaces);
      setAccess(prev => ({ ...prev, overrides: r.data.overrides, surfaces: r.data.surfaces }));
      toast.success("Permissions updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="user-drawer">
      <div
        className="w-full max-w-xl h-full bg-white border-l border-slate-200 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-900 truncate">{user.name || user.email}</div>
            <div className="text-xs text-slate-400 font-mono truncate">{user.email}</div>
          </div>
          <div className="flex items-center gap-2">
            {canWrite && (
              <Button size="sm" onClick={() => save()} disabled={saving} data-testid="btn-save-user-drawer">
                <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? "Saving…" : "Save"}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="btn-close-user-drawer"><X className="w-4 h-4" /></Button>
          </div>
        </div>

        {passcodePrompt && (
          <PasscodeDialog
            label={`change ${user.name || user.email}'s role`}
            onClose={() => setPasscodePrompt(false)}
            onVerified={(token) => { setPasscodePrompt(false); save(token); }}
          />
        )}

        <div className="px-2 pt-2">
          <SubTabs items={TABS} value={tab} onChange={setTab} testId="user-drawer-tabs" testIdPrefix="user-drawer-tab" />
        </div>

        <div className="px-5 py-4 space-y-4">
          {tab === "identity" && (
            <>
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} disabled={!canWrite} className="h-9" data-testid="input-user-name" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)} disabled={!canWrite}>
                  <SelectTrigger className="h-9 text-sm" data-testid="select-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-start gap-2 p-2.5 rounded-md border border-slate-200 cursor-pointer">
                <input type="checkbox" className="mt-0.5 w-4 h-4 accent-slate-900" checked={form.login_enabled}
                  onChange={(e) => set("login_enabled", e.target.checked)} disabled={!canWrite}
                  data-testid="checkbox-login-enabled" />
                <span className="text-xs leading-snug">
                  <span className="font-semibold text-slate-700">Sign-in enabled</span>
                  <span className="block text-slate-400">
                    Off = roster only. They stay selectable as an Account Manager / Project Manager
                    everywhere in the app but cannot sign in until IT adds them to a JumpCloud group.
                  </span>
                </span>
              </label>
              {user.technician_id && (
                <Callout tone="success">
                  Linked technician record — edit skills, scopes, GPS and Miter IDs on the
                  <span className="font-semibold"> Technicians </span> sub-tab.
                </Callout>
              )}
            </>
          )}

          {tab === "contact" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Phone</Label>
                <Input value={form.contact.phone || ""} onChange={(e) => setContact("phone", formatPhoneNumber(e.target.value))} disabled={!canWrite} className="h-9" data-testid="input-user-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Address</Label>
                <Input value={form.contact.address || ""} onChange={(e) => setContact("address", e.target.value)} disabled={!canWrite} className="h-9" data-testid="input-user-address" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.contact.city || ""} onChange={(e) => setContact("city", e.target.value)} disabled={!canWrite} className="h-9" data-testid="input-user-city" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={form.contact.state || ""} onChange={(e) => setContact("state", e.target.value.toUpperCase())} maxLength={2} disabled={!canWrite} className="h-9 uppercase" data-testid="input-user-state" />
                </div>
                <div>
                  <Label className="text-xs">Zip</Label>
                  <Input value={form.contact.zip_code || ""} onChange={(e) => setContact("zip_code", e.target.value)} disabled={!canWrite} className="h-9" data-testid="input-user-zip" />
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input value={form.contact.notes || ""} onChange={(e) => setContact("notes", e.target.value)} disabled={!canWrite} className="h-9" data-testid="input-user-notes" />
              </div>
            </div>
          )}

          {tab === "roster" && (
            <>
              <Callout>
                These flags decide where this person appears in the app. Account Managers show in the
                Proposals and Leads pickers; Project Managers show on Work Orders, invoices and AR
                alerts. Their id never changes, so historical records keep resolving.
              </Callout>
              {[["is_account_manager", "Account Manager", "Selectable as AM on leads and proposals"],
                ["is_project_manager", "Project Manager", "Selectable as PM on work orders and invoices"],
                ["is_default_pm", "Default Project Manager", "Auto-filled on new work orders (only one person)"]].map(([k, label, hint]) => (
                <label key={k} className="flex items-start gap-2 p-2.5 rounded-md border border-slate-200 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 w-4 h-4 accent-slate-900"
                    checked={!!form.roster[k]} onChange={(e) => setRoster(k, e.target.checked)}
                    disabled={!canWrite} data-testid={`checkbox-${k.replace(/_/g, "-")}`} />
                  <span className="text-xs leading-snug">
                    <span className="font-semibold text-slate-700">{label}</span>
                    <span className="block text-slate-400">{hint}</span>
                  </span>
                </label>
              ))}
            </>
          )}

          {tab === "permissions" && (
            !access ? <LoadingState /> : access.error ? (
              <Callout tone="warn">Only admins can view a user's permissions.</Callout>
            ) : (
              <>
                <AccountCheck email={user.email} />
                <UserPermissions access={access} canWrite={canWrite} onSave={saveOverrides} />
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function AccountCheck({ email }) {
  const [diag, setDiag] = useState(null);
  useEffect(() => {
    diagnoseAccount(email).then(r => setDiag(r.data)).catch(() => setDiag({ error: true }));
  }, [email]);
  if (!diag || diag.error) return null;
  const acct = diag.accounts?.[0];
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="account-check">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
        <Stethoscope className="w-3.5 h-3.5" /> Account check
        {acct && (
          <span className="ml-auto font-mono text-[10px] text-slate-400" data-testid="account-check-role">
            server role: {acct.role}{acct.role_customized ? " (customized)" : ""}
          </span>
        )}
      </div>
      {diag.warnings?.length ? diag.warnings.map((w, i) => (
        <Callout key={i} tone="warn"><span data-testid={`account-check-warning-${i}`}>{w}</span></Callout>
      )) : (
        <div className="text-xs text-emerald-700" data-testid="account-check-ok">No account problems found.</div>
      )}
      {acct && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {acct.probes.map(p => (
            <div key={p.path + p.method} className="flex items-center gap-1.5 text-[11px]" title={p.reason || "Allowed"}
              data-testid={`account-probe-${p.method}-${p.path.replace(/[^a-z0-9]+/gi, "-")}`}>
              {p.allowed ? <Check className="w-3 h-3 text-emerald-600 shrink-0" /> : <Ban className="w-3 h-3 text-rose-500 shrink-0" />}
              <span className={p.allowed ? "text-slate-600" : "text-rose-600"}>{p.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UserPermissions({ access, canWrite, onSave }) {
  const [overrides, setOverrides] = useState(access.overrides || {});
  const rows = Object.entries(access.surfaces || {});
  const setOne = (sid, level) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (level === "__role__") delete next[sid]; else next[sid] = level;
      return next;
    });
  };
  return (
    <div className="space-y-3" data-testid="user-permissions">
      <Callout>
        Effective access = role defaults, then any override you set here. Everything is enforced on
        the server too, so hiding a tab is not the only protection.
      </Callout>
      <div className="border border-slate-200 rounded-lg divide-y">
        {rows.map(([sid, level]) => (
          <div key={sid} className="px-3 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`user-perm-row-${sid}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-800 truncate">{sid}</div>
              <div className="text-slate-400">
                role default: {access.role_surfaces?.[sid] || "none"}
                {overrides[sid] ? ` · override: ${overrides[sid]}` : ""}
              </div>
            </div>
            <Select value={overrides[sid] || "__role__"} onValueChange={(v) => setOne(sid, v)} disabled={!canWrite}>
              <SelectTrigger className="h-7 w-32 text-xs shrink-0" data-testid={`select-user-perm-${sid}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__role__">Use role ({access.role_surfaces?.[sid] || "none"})</SelectItem>
                <SelectItem value="none">No Access</SelectItem>
                <SelectItem value="read">Read Only</SelectItem>
                <SelectItem value="write">Read &amp; write</SelectItem>
              </SelectContent>
            </Select>
            <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${
              level === "write" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : level === "read" ? "bg-slate-100 text-slate-600 border-slate-200"
              : "bg-rose-50 text-rose-600 border-rose-200"}`}>{level}</span>
          </div>
        ))}
      </div>
      {canWrite && (
        <Button size="sm" onClick={() => onSave(overrides)} data-testid="btn-save-user-perms">
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Save overrides
        </Button>
      )}
    </div>
  );
}
